/**
 * The **composed mount array** — the ordered `MountData[]` every downstream
 * consumer (`equip_weapon`/`attach_mod`, spawn-freeze, the editor UI) addresses by
 * index. Bonus mounts are a **computed composition over dedicated per-loadout
 * storage**, NOT a stored append into the frame-native mount array. PURE — no
 * mutation, no reducer, no I/O beyond a compendium lookup.
 *
 * The canonical, pinned order (never renumbered by a core-bonus toggle):
 *
 *   [ ...native mounts (loadout.mounts, retrofit-in-place applied),
 *     improvedArmamentMount?,  // cb_improved_armament, <3-native-mount cap
 *     integratedWeaponMount?,  // cb_integrated_weapon, NO cap
 *   ]
 *
 * **Retrofit-in-place** (`cb_mount_retrofitting`): unlike the bonus-mount TAIL above
 * (which appends a whole new mount), a retrofit transforms an EXISTING native mount's presented
 * `type` to `"Main/Aux"` — a computed override, not a stored type mutation.
 * `loadout.retrofit` names which native mount and its pre-retrofit type; this
 * module swaps the type back and forth purely off whether the pilot currently
 * holds the bonus (untaking it reverts the presented type for free, no separate
 * revert command — the same toggle-survival property the bonus-mount tail has).
 * The mount's `slots` are NOT re-derived here — `retrofit_mount` (`reducer.ts`)
 * already reshaped them to the Main/Aux 2-slot layout as a real, permanent storage
 * mutation at retrofit time (surviving an untake; only `undo` reverses it), so this
 * module only ever swaps the `type` string.
 *
 * Native mounts are the **stable index prefix**: every saved `mountIdx` on a
 * command referencing a native mount stays valid across any core-bonus toggle,
 * because bonus mounts only ever append at the tail. `equip_weapon`/`attach_mod`
 * (`reducer.ts`'s `requireComposedSlot`/`writeComposedSlot`) map a composed index
 * back to either native storage (`index < loadout.mounts.length`) or the matching
 * bonus mount's dedicated storage (`index >= loadout.mounts.length`) on write —
 * disambiguated via {@link composedMountProvenance}, since two bonus mounts can
 * occupy the tail.
 *
 * **`<3` cap counts native, non-integrated mounts** (Improved Armament only). RAW:
 * "If your mech has fewer than three mounts (excluding integrated mounts) [...]".
 * `MountData` carries no "integrated" flag today — no frame-native mount is ever
 * the Integrated type in the current model (only a *bonus* mount — Integrated
 * Weapon's grant — would be), so `loadout.mounts.length` alone already excludes
 * integrated mounts. This assumption breaks only if a future frame ships a native
 * Integrated mount, which no core content does today. **Integrated Weapon carries
 * no cap at all** — RAW grants it unconditionally to whoever holds the bonus.
 *
 * `composedMounts` and {@link composedMountProvenance} both derive from ONE
 * internal walk ({@link bonusMountTail}) so the tail order and the storage-field
 * mapping can never drift apart from each other.
 */
import type { LoadoutData, MountData, PilotData } from "./types";
import { slotSizesForMount } from "./mounts";

/**
 * The compendium `core_bonus` id for Mount Retrofitting. Unlike
 * {@link resolveBonusMountGrant}'s structural `add_mount`-row read, this bonus
 * carries NO `bonuses` row at all — it is a player choice driven by
 * `retrofit_mount`, with nothing structural to generalize over, so the gate is a
 * literal id check, both here and in `reducer.ts`'s
 * `retrofit_mount`/`un_retrofit_mount`.
 */
export const MOUNT_RETROFITTING_BONUS_ID = "cb_mount_retrofitting";

/** Named arguments for {@link applyRetrofitInPlace} (kept under `max-params-3`). */
type ApplyRetrofitInPlaceArgs = {
  loadout: LoadoutData;
  pilot: PilotData;
  mount: MountData;
  index: number;
};

/**
 * Apply the retrofit-in-place type swap to ONE native mount, if `index` is the
 * mount `loadout.retrofit` names AND the pilot currently holds
 * {@link MOUNT_RETROFITTING_BONUS_ID}. Otherwise returns `mount` unchanged
 * (verbatim pass-through — the stable-index-prefix discipline every other native
 * mount already gets). Only the `type` string swaps to `"Main/Aux"`; `slots` are
 * left exactly as stored (`retrofit_mount` already reshaped them for real at
 * retrofit time — see the module doc-comment).
 */
function applyRetrofitInPlace(args: ApplyRetrofitInPlaceArgs): MountData {
  const { loadout, pilot, mount, index } = args;
  const retrofit = loadout.retrofit;
  if (!retrofit || retrofit.mountIdx !== index) return mount;
  if (!pilot.coreBonuses.includes(MOUNT_RETROFITTING_BONUS_ID)) return mount;
  return { ...mount, type: "Main/Aux" };
}

/**
 * The read-only compendium slice this module needs: resolve a core-bonus id to
 * its raw `bonuses` rows. A structural slice (mirrors `loadoutInvariants.ts`'s
 * `LoadoutInvariantCompendium` / `sp.ts`'s `SpCompendium`) so `@lancer-vtt/domain`
 * leans on the compendium's *types* only — the real `Compendium`
 * (`@lancer-vtt/compendium`) satisfies it structurally. Kept separate from
 * `LoadoutInvariantCompendium` (different `getById` tenant).
 */
export type CoreBonusCompendium = {
  getById(
    type: "core_bonus",
    id: string,
  ): { bonuses?: { id?: unknown; val?: unknown; max?: unknown }[] } | undefined;
};

/**
 * Which storage field a composed tail mount reads from/writes to, plus the
 * granting bonus's id and its `no_mods` flag — the shared provenance shape
 * {@link composedMountProvenance} returns per composed index. `reducer.ts`'s
 * `writeComposedSlot` uses `storageField` to route a tail write to the right
 * dedicated field; `attach_mod`'s `no_mods` gate reads `noMods` off it directly,
 * rather than hardcoding a `cb_integrated_weapon` id check.
 */
export type BonusMountProvenance = {
  storageField: "improvedArmamentMount" | "integratedWeaponMount";
  bonusId: string;
  noMods: boolean;
};

/** One resolved bonus-mount grant: which bonus granted it, its cap (if any), and whether it's no-mods. */
type BonusMountGrant = {
  bonusId: string;
  /** The `add_mount` row's `max`, if it carries one (Improved Armament's `<3`); `undefined` = uncapped. */
  max?: number;
  noMods: boolean;
};

/**
 * Resolve the FIRST held core bonus that carries a structural `add_mount`/`val`
 * row matching `val` (e.g. `"flex"` or `"aux"`) — the same
 * read-the-compendium-row discipline `compute.ts`'s `collectPilotBonuses` and
 * `spawn.ts`'s `resolvePilotRangeBonus` use, rather than a hardcoded core-bonus id
 * check. This means a homebrew LCP core bonus shaped the same way composes
 * identically, for free. `noMods` is read off the SAME bonus's `{id:"no_mods",
 * val:true}` row, if any — a bonus mount's no-mods-ness is a property of its
 * granting bonus, not a hardcoded id.
 *
 * Returns `undefined` if no held bonus carries a matching `add_mount` row.
 */
function resolveBonusMountGrant(
  pilot: PilotData,
  compendium: CoreBonusCompendium,
  val: string,
): BonusMountGrant | undefined {
  for (const bonusId of pilot.coreBonuses) {
    const cb = compendium.getById("core_bonus", bonusId);
    const rows = cb?.bonuses ?? [];
    const addMountRow = rows.find((row) => row && row.id === "add_mount" && row.val === val);
    if (!addMountRow) continue;
    const noMods = rows.some((row) => row && row.id === "no_mods" && row.val === true);
    return {
      bonusId,
      max: typeof addMountRow.max === "number" ? addMountRow.max : undefined,
      noMods,
    };
  }
  return undefined;
}

/** An empty mount of `type` (no weapon in any slot), sized off the mount vocabulary. */
function emptyMount(type: string): MountData {
  const slots = slotSizesForMount(type) ?? [];
  return { type, slots: slots.map(() => ({ weaponId: null })) };
}

/**
 * Pad a mount's stored `slots` array up to
 * the length its (composed, post-retrofit) `type` calls for — **never truncate**.
 * A stored array longer than its type's table would mean silently deleting an
 * equipped weapon, so a too-long array (which no planned table change produces
 * today) is passed through unchanged rather than shortened.
 *
 * This is what makes a RAW table correction (e.g. Flex growing from one slot to
 * two) reach a mech someone already built and saved BEFORE the correction
 * landed, with no migration and no re-seed: `slotSizesForMount` is the single
 * source of truth for slot COUNT, and every mount composed through this module —
 * native (after {@link applyRetrofitInPlace}) and the bonus-mount tail alike —
 * is normalized against it here, once. An unknown mount type (`slotSizesForMount`
 * returns `undefined`) is left exactly as stored — there is no table to pad
 * against, and `weaponFitsSlot`/`requireComposedSlot` already treat an unknown
 * type as admitting nothing.
 */
function normalizeMountSlots(mount: MountData): MountData {
  const wanted = slotSizesForMount(mount.type)?.length;
  if (wanted === undefined || mount.slots.length >= wanted) return mount;
  const padding = Array.from({ length: wanted - mount.slots.length }, () => ({ weaponId: null }));
  return { ...mount, slots: [...mount.slots, ...padding] };
}

/** One resolved bonus-mount tail entry: its provenance plus the actual `MountData` to append. */
type BonusMountTailEntry = BonusMountProvenance & { mount: MountData };

/**
 * The ONE internal walk that decides the bonus-mount tail — both
 * {@link composedMounts} and {@link composedMountProvenance} derive from this, so
 * the order and the storage mapping can never drift apart. Canonical order:
 * Improved Armament, then Integrated Weapon.
 */
function bonusMountTail(
  loadout: LoadoutData,
  pilot: PilotData,
  compendium: CoreBonusCompendium,
): BonusMountTailEntry[] {
  const tail: BonusMountTailEntry[] = [];

  const improvedArmament = resolveBonusMountGrant(pilot, compendium, "flex");
  if (improvedArmament !== undefined) {
    const cap = improvedArmament.max ?? Number.POSITIVE_INFINITY;
    if (loadout.mounts.length < cap) {
      tail.push({
        storageField: "improvedArmamentMount",
        bonusId: improvedArmament.bonusId,
        noMods: improvedArmament.noMods,
        mount: loadout.improvedArmamentMount ?? emptyMount("Flex"),
      });
    }
  }

  // Integrated Weapon: `add_mount`/`val:"aux"`. NO cap — RAW grants it
  // unconditionally to whoever holds the bonus (unlike Improved Armament's `<3`).
  const integratedWeapon = resolveBonusMountGrant(pilot, compendium, "aux");
  if (integratedWeapon !== undefined) {
    tail.push({
      storageField: "integratedWeaponMount",
      bonusId: integratedWeapon.bonusId,
      noMods: integratedWeapon.noMods,
      mount: loadout.integratedWeaponMount ?? emptyMount("Aux"),
    });
  }

  return tail;
}

/**
 * Compose the ordered mount array a loadout's owning mech presents to every
 * index-addressing consumer. See the module doc-comment for the full order and
 * cap discipline. PURE — reads `loadout`/`pilot`/`compendium`, mutates nothing,
 * and is safe to call on every render/compute pass.
 */
export function composedMounts(
  loadout: LoadoutData,
  pilot: PilotData,
  compendium: CoreBonusCompendium,
): MountData[] {
  // Native mounts are the stable index prefix — retrofit-in-place transforms an
  // entry's presented `type` here without changing this array's length or order
  // (still one entry per `loadout.mounts` index).
  const nativeMounts = loadout.mounts.map((mount, index) =>
    applyRetrofitInPlace({ loadout, pilot, mount, index }),
  );
  const all = [...nativeMounts, ...bonusMountTail(loadout, pilot, compendium).map((t) => t.mount)];
  // Normalize slot COUNT over the whole composed array — native mounts and
  // the bonus-mount tail alike — so a stored mount minted before a table
  // correction (e.g. a 1-slot Flex) reaches its full slot count without a
  // migration. See `normalizeMountSlots`.
  return all.map(normalizeMountSlots);
}

/**
 * The provenance of every composed index — `null` for a native mount (index `<
 * loadout.mounts.length`), or a {@link BonusMountProvenance} for a bonus-mount
 * tail slot. Parallel to {@link composedMounts}' own return (same length, same
 * order — both derive from {@link bonusMountTail}), so a caller can zip the two
 * or just index the same `mountIdx` into each. `reducer.ts`'s `writeComposedSlot`
 * uses this to route a tail write to the RIGHT dedicated storage field, since
 * two bonus mounts can occupy the tail; `attach_mod`'s `no_mods` gate reads
 * `noMods` off the entry at `cmd.mountIdx`.
 */
export function composedMountProvenance(
  loadout: LoadoutData,
  pilot: PilotData,
  compendium: CoreBonusCompendium,
): (BonusMountProvenance | null)[] {
  const tail = bonusMountTail(loadout, pilot, compendium);
  return [
    ...loadout.mounts.map(() => null),
    ...tail.map((t) => ({ storageField: t.storageField, bonusId: t.bonusId, noMods: t.noMods })),
  ];
}
